import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeLang } from '@/utils/approval/i18nText';
import CheckIcon from '@/public/icons/approval/check.svg';
import DeactivatedBadge from '@/components/Approval/Common/DeactivatedBadge';
import OnLeaveBadge from '@/components/Approval/Common/OnLeaveBadge';
import { resolveDepartmentName } from '@/utils/approval/ceoAffiliation';
import { formatCardDate, pickLang } from './statusMapping';
import {
  ApprovalLineCardHeader,
  ApprovalLineCardHeaderDept,
  ApprovalLineCardHeaderPosition,
  ApprovalLineCardName,
  ApprovalLineCardRoot,
  ApprovalLineCardStatus,
  ApprovalLineCardStatusChip,
} from './styles';
import type { ApprovalViewAuthor } from './types';

/**
 * 결재라인 row 맨 앞 "작성자" 카드 — ApprovalLineCard 셸 재사용, 순번 chip 비움 + 하단 상신일.
 * v2 approvers 배열에 작성자가 포함되지 않아 별도 카드(권위: v2 author 객체).
 */

type AuthorCardProps = {
  author: ApprovalViewAuthor;
  /**
   * 문서 상신일(v2 authoredAt) — 하단 chip 에 '✓ YYYY-MM-DD' 로 노출.
   * 임시저장은 상신 전이라 값이 없어 '상신' 텍스트 폴백.
   */
  authoredAt?: string | null;
  /** 퇴사자 여부 — 이름 옆 '퇴사' 배지 */
  isDeactivated?: boolean;
  /** 휴직자 여부 — 이름 옆 '휴직' 배지. 퇴사와 겹치면 퇴사만 표시한다 */
  isOnLeave?: boolean;
};

const AuthorCard = ({
  author,
  authoredAt,
  isDeactivated = false,
  isOnLeave = false,
}: AuthorCardProps) => {
  const { t, i18n } = useTranslation();
  const lang = useMemo(() => normalizeLang(i18n.language), [i18n.language]);

  const name = pickLang(author.name, lang);
  const position = pickLang(author.positionName, lang);
  const department = resolveDepartmentName(author, author.departmentName) ?? '';
  const submittedAt = formatCardDate(authoredAt);

  return (
    <ApprovalLineCardRoot>
      {department && (
        <ApprovalLineCardHeader>
          <ApprovalLineCardHeaderDept>{department}</ApprovalLineCardHeaderDept>
        </ApprovalLineCardHeader>
      )}
      {position && (
        <ApprovalLineCardHeaderPosition>
          {position}
        </ApprovalLineCardHeaderPosition>
      )}
      <ApprovalLineCardName>
        {isDeactivated && <DeactivatedBadge />}
        {!isDeactivated && isOnLeave && <OnLeaveBadge />}
        {name}
      </ApprovalLineCardName>
      <ApprovalLineCardStatus>
        <ApprovalLineCardStatusChip
          tone="neutral"
          isCompleted={Boolean(submittedAt)}
        >
          {submittedAt ? (
            <>
              <CheckIcon
                width={12}
                height={12}
                style={{ marginRight: 4, color: 'var(--dk-text-strong, #555)' }}
              />
              {submittedAt}
            </>
          ) : (
            t('상신')
          )}
        </ApprovalLineCardStatusChip>
      </ApprovalLineCardStatus>
    </ApprovalLineCardRoot>
  );
};

export default AuthorCard;
